import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Caravan, FileText, KeyRound, Wallet, Loader2, Warehouse, ArrowRight, AlertTriangle,
} from 'lucide-react';
import * as api from '../../lib/api';
import { useAuthStore } from '../../store/useAuthStore';
import { formatCents, formatDate } from '../../lib/format';

/**
 * The tenant's landing page.
 *
 * It was four tiles reading "—" above a paragraph promising the portal was
 * coming. This is a paying customer's first screen, so it now answers the
 * questions they actually arrive with: what do I owe, what is my gate code,
 * and where is my rig.
 */
export default function AccountHome() {
  const user = useAuthStore((s) => s.user);
  const [leases, setLeases] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [vehicles, setVehicles] = useState(null);

  useEffect(() => {
    let live = true;
    // Three independent requests. One failing must not blank the other two —
    // a tenant who cannot load their invoices should still see their gate code.
    api.listLeases().then((p) => live && setLeases(p.leases)).catch(() => live && setLeases([]));
    api.listMyInvoices(50).then((p) => live && setInvoices(p.invoices)).catch(() => live && setInvoices([]));
    api.listVehicles().then((p) => live && setVehicles(p.vehicles)).catch(() => live && setVehicles([]));
    return () => { live = false; };
  }, []);

  const loading = leases === null || invoices === null || vehicles === null;

  const active = (leases ?? []).filter((l) => ['active', 'past_due', 'ending'].includes(l.status));
  const owed = (invoices ?? [])
    .filter((i) => ['open', 'past_due'].includes(i.status))
    .reduce((sum, i) => sum + Number(i.balance_cents), 0);
  const overdue = (invoices ?? []).filter(
    (i) => i.status === 'past_due' && Number(i.balance_cents) > 0);
  const withCode = active.filter((l) => l.gate_code);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
        Welcome back, {user?.first_name}
      </h1>
      <p className="mt-1 text-sm text-slate-600">Your spaces, vehicles, and billing.</p>

      {loading ? (
        <div className="grid place-items-center py-20" role="status" aria-label="Loading your account">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
        </div>
      ) : (
        <>
          {/* Anything overdue goes above the tiles. It is the one thing on this
              page that needs acting on rather than reading. */}
          {overdue.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4">
              <p className="flex items-start gap-2.5 text-sm text-rose-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
                <span>
                  <strong className="font-bold">
                    {formatCents(overdue.reduce((s, i) => s + Number(i.balance_cents), 0))} is past due
                  </strong>
                  <span className="mt-0.5 block text-xs">
                    Being behind does not lock you out of the gate — but please settle it when you can.
                  </span>
                </span>
              </p>
              <Link to="/account/billing"
                    className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700">
                Pay now
              </Link>
            </div>
          )}

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Tile Icon={Warehouse} label="Spaces rented" value={String(active.length)}
                  hint={active.map((l) => l.unit_number).join(', ') || 'None yet'}
                  to="/account/spaces" />
            <Tile Icon={KeyRound} label="Gate access"
                  value={withCode.length ? 'Ready' : '—'}
                  hint={withCode.length ? 'See your codes' : 'Issued on move-in'}
                  to="/account/spaces" />
            <Tile Icon={Wallet} label="Balance due" value={formatCents(owed)}
                  hint={owed > 0 ? 'Across all spaces' : 'Nothing outstanding'}
                  to="/account/billing" tone={owed > 0 ? 'bad' : 'good'} />
            <Tile Icon={Caravan} label="Vehicles" value={String(vehicles.length)}
                  hint={vehicles.length ? 'Used to check fit' : 'Add one to check fit'}
                  to="/account/vehicles" />
          </div>

          {active.length > 0 && (
            <>
              <h2 className="mt-8 text-sm font-bold text-slate-900">Your spaces</h2>
              <ul className="mt-3 space-y-2">
                {active.map((l) => (
                  <li key={l.public_id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        {l.facility_name} · space {l.unit_number}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        Since {formatDate(l.starts_on)} · {formatCents(l.monthly_rate_cents)}/month
                      </p>
                    </div>
                    {l.gate_code && (
                      <Link to="/account/spaces"
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50">
                        <KeyRound className="h-3.5 w-3.5 text-brand-600" aria-hidden="true" />
                        Gate codes
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}

          {active.length === 0 && (
            <div className="mt-8 rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center">
              <Warehouse className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
              <p className="mt-3 text-sm font-bold text-slate-900">You are not renting a space yet</p>
              <p className="mt-1 text-sm text-slate-500">
                Tell us your rig's length and we will show you what fits.
              </p>
              <Link to="/search"
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-700">
                Find a space <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          )}

          {invoices.length > 0 && (
            <div className="mt-8 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900">Recent billing</h2>
              <Link to="/account/billing" className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 hover:underline">
                All invoices <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            </div>
          )}
          {invoices.slice(0, 3).map((i) => (
            <div key={i.public_id}
                 className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{i.invoice_number}</p>
                <p className="text-xs text-slate-500">Due {formatDate(i.due_on)}</p>
              </div>
              <p className={`text-sm font-bold tabular-nums ${
                Number(i.balance_cents) > 0 ? 'text-rose-700' : 'text-slate-400'}`}>
                {Number(i.balance_cents) > 0 ? formatCents(i.balance_cents) : 'Paid'}
              </p>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Tile({ Icon, label, value, hint, to, tone, mono }) {
  const valueTone = tone === 'bad' ? 'text-rose-700' : tone === 'good' ? 'text-slate-900' : 'text-slate-900';
  const inner = (
    <>
      <Icon className="h-5 w-5 text-brand-600" aria-hidden="true" />
      <p className={`mt-3 truncate text-2xl font-extrabold ${valueTone} ${mono ? 'font-mono tracking-widest' : ''}`}>
        {value}
      </p>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 truncate text-[12px] text-slate-400">{hint}</p>
    </>
  );
  const className = 'block rounded-xl border border-slate-200 bg-white p-4 transition hover:border-slate-300 hover:shadow-card';
  return to ? <Link to={to} className={className}>{inner}</Link> : <div className={className}>{inner}</div>;
}
